/**
 * The booking funnel: eight steps from first view to review.
 *
 * Three things this screen is careful about:
 *   - **One grain at a time.** Setting a city clears the puja filter and says so. A funnel
 *     filtered by both reads as a fact about neither, because the rollup stores each grain as
 *     its own row rather than as an intersection.
 *   - **No per-step user list.** Journey events are pseudonymous and this console does not
 *     pierce that. To act on an individual, work from the booking or the support ticket.
 *   - **Which steps the server vouches for comes from the API.** `server_verified` is derived
 *     from the event registry, not restated here — an earlier hard-coded copy was already
 *     wrong about `review_submitted`.
 *
 * Conversion percentages ARE computed here, from the API's integer counts. That is the
 * documented exception: percentages of counts, never money.
 */
import { useState } from 'react';
import { Button, Card, Chip, Icon, Kicker, StatusChip } from '../components/primitives';
import { Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable } from '../components/data';
import { Section } from '../components/kit';
import { FunnelBars } from '../components/charts';
import { pctOf } from '../lib/pct';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useFunnel, type FunnelStep } from '../lib/api/hooks/analytics';
import { useCities } from '../lib/api/hooks/geo';
import { usePujas } from '../lib/api/hooks/catalogue';
import { resolveWindow, useWindowSearch } from '../app/urlState';

/** Below this share continuing, a step is the funnel's biggest problem, not a normal narrowing. */
const DROP_PCT = 60;

/**
 * How one transition reads, found live: steps are counted from separate events, so the counts
 * are not a strict funnel. A step at zero with events after it was never counted — not a drop —
 * and a step above the one before ("301.4% continue") is not a rate anybody continued at.
 * Neither may be painted as a drop or named the biggest one.
 */
export function stepReading(
  steps: readonly { count: number; conversion_from_previous?: number | null }[],
  index: number,
): { kind: 'none' } | { kind: 'uncounted' } | { kind: 'over' } | { kind: 'rate'; pct: number } {
  const s = steps[index]!;
  if (s.count === 0 && steps.slice(index + 1).some((later) => later.count > 0)) return { kind: 'uncounted' };
  const c = s.conversion_from_previous;
  if (c === null || c === undefined) return { kind: 'none' };
  if (c > 1) return { kind: 'over' };
  return { kind: 'rate', pct: Math.round(c * 1000) / 10 };
}

/** The step the product exists for: a *named* pandit chosen, not an anonymous dispatch. */
export const HERO_STEP = 'pandit.selected';

export const WINDOWS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
] as const;

/** `funnel.<underscore slug>` — the rollup key, matching what the server stores. */
export function stepMetricKey(step: string): string {
  return `funnel.${step.replace(/\./g, '_')}`;
}

/** The window as the API wants it: two ISO dates, inclusive. */
export function windowDates(days: number, today = new Date()): { from: string; to: string } {
  const to = new Date(today);
  const from = new Date(today);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

/** One CSV field: quoted only when it has to be, with any quote inside doubled (RFC 4180). */
export function csvField(value: string | number | boolean | null): string {
  const text = value === null ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * The funnel as it stands on screen, as CSV — the design's "Export CSV".
 *
 * The steps are already in hand and are the whole answer (eight rows, never paged), so the file is
 * built here rather than by a second read that could disagree with what the operator is looking
 * at. Conversion is the raw fraction, not a rounded percentage: a spreadsheet does its own maths.
 */
export function funnelCsv(steps: readonly FunnelStep[]): string {
  const header = ['step', 'label', 'count', 'conversion_from_previous', 'server_verified'];
  const rows = steps.map((s) =>
    [s.step, s.label, s.count, s.conversion_from_previous, s.server_verified].map(csvField).join(','),
  );
  return [header.join(','), ...rows].join('\n') + '\n';
}

/** `bookmypandiit-funnel-2026-09-24-2026-10-23[-patna].csv` — the window, and the one grain if any (doc 05 §2). */
export function funnelFileName(from: string, to: string, grain: string): string {
  const slug = grain.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `bookmypandiit-funnel-${from}-${to}${slug ? `-${slug}` : ''}.csv`;
}

export function download(name: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

export function Funnel() {
  const { go } = useConsole();
  /* Window, city and puja live in the URL (doc 05 §2): a pasted link reopens this exact view. */
  const [search, setSearch] = useWindowSearch();
  const [hint, setHint] = useState('');

  const cities = useCities({ page: 1, page_size: 100 });
  const pujas = usePujas({ page: 1, page_size: 100 });
  const cityItems = cities.data?.items ?? [];
  const pujaItems = pujas.data?.items ?? [];

  /* One grain at a time, even from a pasted URL that names both: the city wins, as the first
     filter on screen. */
  const cityId = search.city_id;
  const pujaId = cityId === undefined ? search.puja_id : undefined;
  /* The Select takes plain strings, so the operator picks a NAME and we map it to and from the id
     the URL and the API carry. One list does both directions, so they cannot disagree. */
  const city = cityItems.find((c) => c.id === cityId)?.name ?? '';
  const puja = pujaItems.find((p) => p.id === pujaId)?.name_en ?? '';

  const pickCity = (v: string) => {
    const id = cityItems.find((c) => c.name === v)?.id;
    setSearch({ city_id: id, puja_id: id === undefined ? pujaId : undefined });
    setHint(id !== undefined && pujaId !== undefined ? 'Puja filter cleared — the funnel reads one grain at a time.' : '');
  };
  const pickPuja = (v: string) => {
    const id = pujaItems.find((p) => p.name_en === v)?.id;
    setSearch({ puja_id: id, city_id: id === undefined ? cityId : undefined });
    setHint(id !== undefined && cityId !== undefined ? 'City filter cleared — the funnel reads one grain at a time.' : '');
  };

  const dates = resolveWindow(
    search,
    WINDOWS.map((w) => w.days),
    30,
    new Date(),
  );
  const pickWindow = (days: number) => {
    const w = windowDates(days);
    setSearch({ date_from: w.from, date_to: w.to });
  };
  /* Filtering is the SERVER's: the rollup holds a row per grain, and a client-side narrowing
     of the all-cities row would be arithmetic on a number that already excluded the detail. */
  const funnel = useFunnel({
    date_from: dates.from,
    date_to: dates.to,
    ...(cityId !== undefined ? { city_id: cityId } : {}),
    ...(pujaId !== undefined ? { puja_id: pujaId } : {}),
  });

  const steps: readonly FunnelStep[] = funnel.data?.steps ?? [];
  /* The single worst transition, not every step under the threshold. Labelling three steps
     "biggest drop" tells an operator to fix all of them at once, which is the opposite of what
     a funnel is for — and two steps can tie at the same percentage. */
  const worstStep = steps.reduce<string | null>((worst, s, i) => {
    const reading = stepReading(steps, i);
    if (reading.kind !== 'rate' || reading.pct >= DROP_PCT) return worst;
    const current = steps.find((x) => x.step === worst)?.conversion_from_previous ?? 1;
    return reading.pct / 100 < current ? s.step : worst;
  }, null);
  const first = steps[0]?.count ?? 0;
  const overall = pctOf(steps[steps.length - 1]?.count ?? 0, first);
  const empty = steps.length > 0 && steps.every((s) => !s.count);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {WINDOWS.map((w) => (
            <Chip
              key={w.label}
              variant="filter"
              label={w.label}
              selected={dates.days === w.days}
              onClick={() => pickWindow(w.days)}
            />
          ))}
        </div>
        <Select
          density="admin"
          placeholder="City"
          value={city}
          onChange={pickCity}
          options={(cities.data?.items ?? []).map((c) => c.name)}
          style={{ width: 150 }}
        />
        <Select
          density="admin"
          placeholder="Puja"
          value={puja}
          onChange={pickPuja}
          options={(pujas.data?.items ?? []).map((p) => p.name_en)}
          style={{ width: 190 }}
        />
        {hint && <StatusChip tone="info" label={hint} icon="info" />}
        <span style={{ flex: 1 }} />
        <Button size="sm" variant="ghost" icon="refresh-cw" onClick={() => void funnel.refetch()}>
          Refresh
        </Button>
        <Button
          size="sm"
          variant="secondary"
          icon="download"
          disabled={steps.length === 0}
          onClick={() => download(funnelFileName(dates.from, dates.to, city || puja), funnelCsv(steps))}
        >
          Export CSV
        </Button>
      </div>

      {funnel.isPending && <LoadingState message="Reading the rollup…" />}
      {funnel.isError && (
        <ErrorState title="Could not read the funnel" cause={errorMessage(funnel.error)} />
      )}

      {funnel.isSuccess && empty && (
        <Card variant="flat" padding="var(--sp-xxl)">
          <EmptyState
            icon="chart-no-axes-column"
            title="No journey events in this window"
            body="Nothing was recorded for the filters you picked. Widen the window, or clear the city and puja filters."
          />
        </Card>
      )}

      {funnel.isSuccess && !empty && (
        <>
          <Card variant="flat" padding="var(--sp-lg)" style={{ display: 'grid', gap: 'var(--sp-lg)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-md)', flexWrap: 'wrap' }}>
              <Kicker>Overall conversion · viewed to review ·{' '}
                {dates.days ? `last ${dates.days} days` : `${dates.from} – ${dates.to}`}
              </Kicker>
              <span style={{ flex: 1 }} />
              <span
                className="tabular"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 26,
                  lineHeight: '32px',
                  fontWeight: 'var(--fw-bold)',
                  color: 'var(--c-ink)',
                }}
              >
                {overall}%
              </span>
            </div>

            <FunnelBars
              onSelect={() => go('metrics')}
              bars={steps.map((s, i) => {
                /* The API already computed this as a ratio; rendering its own number rather
                   than recomputing keeps the bar and the table telling one story. */
                const reading = stepReading(steps, i);
                const step = reading.kind === 'rate' ? reading.pct : null;
                const drop = step !== null && step < DROP_PCT;
                return {
                  key: s.step,
                  label: (
                    <>
                      {s.label}
                      {s.server_verified && <Icon name="shield-check" size={12} />}
                    </>
                  ),
                  count: s.count,
                  widthPct: pctOf(s.count, first),
                  emphasis: s.step === HERO_STEP,
                  note:
                    reading.kind === 'uncounted'
                      ? { text: 'not counted — later steps have events', alarm: false }
                      : reading.kind === 'over'
                        ? { text: 'more than the step before — not a strict funnel', alarm: false }
                        : step === null
                          ? null
                          : {
                          text: `${step}% continue${drop ? (s.step === worstStep ? ' · biggest drop' : ' · steep') : ''}`,
                          alarm: drop,
                        },
                };
              })}
            />
          </Card>

          <Section
            title="Step table"
            note="Cumulative conversion is computed here from the API's counts — integer arithmetic, no money"
            pad={false}
            actions={
              <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                Click a step to open it per-city in the explorer
              </span>
            }
          >
            <DataTable
              density="compact"
              onRowClick={() => go('metrics')}
              columns={[
                { key: 'label', label: 'Step' },
                { key: 'metric', label: 'Metric key', mono: true },
                { key: 'count', label: 'Count', align: 'right', mono: true },
                { key: 'step', label: 'From previous', align: 'right', mono: true },
                { key: 'cum', label: 'Cumulative', align: 'right', mono: true },
                { key: 'note', label: '' },
              ]}
              rows={steps.map((s, i) => {
                const reading = stepReading(steps, i);
                const step = reading.kind === 'rate' ? reading.pct : null;
                return {
                  id: s.step,
                  label: s.label,
                  metric: stepMetricKey(s.step),
                  count: s.count.toLocaleString('en-IN'),
                  step: (
                    <span style={{ color: step !== null && step < DROP_PCT ? 'var(--c-error-text)' : 'var(--c-ink)' }}>
                      {reading.kind === 'uncounted'
                        ? 'not counted'
                        : reading.kind === 'over'
                          ? 'above 100%'
                          : step === null
                            ? '—'
                            : `${step}%`}
                    </span>
                  ),
                  cum: `${pctOf(s.count, first)}%`,
                  note: s.server_verified ? (
                    <StatusChip tone="success" label="server-verified" icon="shield-check" />
                  ) : s.step === HERO_STEP ? (
                    <StatusChip tone="info" label="named-provider moment" />
                  ) : null,
                };
              })}
              footer={
                <>
                  <span>{steps.length} steps · order comes from the registry and is never re-sorted</span>
                  <span className="tabular">{overall}% end to end</span>
                </>
              }
            />
          </Section>

          <div
            style={{
              background: 'var(--c-sunk)',
              border: '1px solid var(--c-border)',
              borderRadius: 'var(--r-tile)',
              padding: 'var(--sp-md) var(--sp-lg)',
              fontSize: 'var(--fs-admin-cell)',
              color: 'var(--c-ink2)',
            }}
          >
            There is deliberately no per-step user list. Journey events are pseudonymous, and this
            console does not pierce that. To act on an individual, work from the booking or the
            support ticket.
          </div>
        </>
      )}
    </>
  );
}
